import { useMemo } from 'react';

import { css, SerializedStyles } from '@emotion/react';
import { Box, Stack } from '@mui/system';
import { Types, useTranslation } from '@scorenco/core';

import { EventPhaseCard } from '../../containers';
import { T_KEYS } from '../../translations/keys';

const getGameTypes = (step: number): number[] | null => {
  if (step === 1) {
    return [1];
  }

  if (step === 2) {
    return [2];
  }

  if (step === 3) {
    return [4];
  }

  if (step === 4) {
    return [9];
  }

  return null;
};

interface GameWrapper {
  game: Types.Tournaments_Game;
}

const toGameId = (game?: Types.Tournaments_Game): number | undefined =>
  typeof game?.id === 'number' ? game.id : undefined;

const getWinnerOfGameId = (
  engagement: Types.Tournaments_Engagement | undefined
): number | undefined => {
  const winnerId = (engagement as { winner_of_game_id?: unknown } | undefined)
    ?.winner_of_game_id;
  return typeof winnerId === 'number' ? winnerId : undefined;
};

const sortRoundByWinnerLink = (
  roundGames: GameWrapper[],
  nextRoundGames: GameWrapper[]
) => {
  const nextRoundOrder = new Map<number, number>();
  nextRoundGames.forEach((wrapper, index) => {
    const gameId = toGameId(wrapper.game);
    if (typeof gameId === 'number') {
      nextRoundOrder.set(gameId, index);
    }
  });

  return roundGames
    .map((wrapper, index) => {
      const currentGameId = toGameId(wrapper.game);
      if (typeof currentGameId !== 'number') {
        return { wrapper, sortKey: Number.MAX_SAFE_INTEGER + index };
      }

      for (const nextWrapper of nextRoundGames) {
        const nextGameId = toGameId(nextWrapper.game);
        if (typeof nextGameId !== 'number') {
          continue;
        }

        const winnerInHome = getWinnerOfGameId(nextWrapper.game?.home_engagement);
        if (winnerInHome === currentGameId) {
          const nextIndex = nextRoundOrder.get(nextGameId) ?? Number.MAX_SAFE_INTEGER;
          return { wrapper, sortKey: nextIndex * 2 };
        }

        const winnerInAway = getWinnerOfGameId(nextWrapper.game?.away_engagement);
        if (winnerInAway === currentGameId) {
          const nextIndex = nextRoundOrder.get(nextGameId) ?? Number.MAX_SAFE_INTEGER;
          return { wrapper, sortKey: nextIndex * 2 + 1 };
        }
      }

      return { wrapper, sortKey: Number.MAX_SAFE_INTEGER + index };
    })
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ wrapper }) => wrapper);
};

interface EliminationCupModeProps {
  games?: GameWrapper[];
  tournament?: Types.Tournaments_Tournament;
}

export const EliminationCupMode = ({
  games,
  tournament,
}: EliminationCupModeProps) => {
  const { t } = useTranslation();

  const stepsArray = useMemo(() => {
    const gameTypes = [1, 2, 4, 9] as const;
    const gamesByType = new Map<number, GameWrapper[]>();

    gameTypes.forEach((gameType) => {
      gamesByType.set(
        gameType,
        (games ?? []).filter((wrapper) => wrapper?.game?.game_type === gameType)
      );
    });

    // Ordonne chaque tour d'elimination en se basant sur winner_of_game_id
    // du tour suivant afin d'aligner correctement les branches.
    for (let i = 1; i < gameTypes.length; i++) {
      const currentType = gameTypes[i];
      const nextType = gameTypes[i - 1];
      const currentRoundGames = gamesByType.get(currentType) ?? [];
      const nextRoundGames = gamesByType.get(nextType) ?? [];
      gamesByType.set(
        currentType,
        sortRoundByWinnerLink(currentRoundGames, nextRoundGames)
      );
    }

    return Array(5)
      .fill(null)
      .map((_, i) => ({
        stepNumber: i + 1,
        games: getGameTypes(i + 1)?.map(
          (gameType) => gamesByType.get(gameType) ?? []
        ),
      }))
      .filter((step) => (step?.games?.[0]?.length ?? 0) > 0)
      .reverse();
  }, [games]);

  const firstStepLength = stepsArray[0]?.stepNumber;

  return (
    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
      {stepsArray?.map((step, i) => {
        const totalGameLength =
          step.games
            ?.map((games) => games?.length ?? 0)
            ?.reduce((a, b) => (a ?? 0) + (b ?? 0), 0) ?? 0;

        if (totalGameLength === 0) {
          return null;
        }

        let height: SerializedStyles | undefined;
        let step_style: SerializedStyles | undefined;

        if (totalGameLength === 1) {
          height = css`
            height: auto;
          `;
        } else if (firstStepLength === 2) {
          height = css`
            height: 30rem;
          `;
        } else if (
          step?.games?.[0]?.[0]?.game?.name?.includes('Demi') &&
          firstStepLength === 3
        ) {
          height = css`
            height: 48rem;
          `;
        } else if (totalGameLength === 2) {
          height = css`
            height: 47rem;
          `;
        } else if (firstStepLength === 3) {
          height = css`
            height: 65rem;
          `;
        } else if (totalGameLength === 4) {
          height = css`
            height: 95rem;
          `;
        } else if (totalGameLength === 8) {
          height = css`
            height: 110rem;
          `;
        }

        return (
          <Box
            key={i}
            sx={{
              display: 'flex',
              flexFlow: 'row wrap',
              alignItems: 'center',
              ml: '0 !important',
              position: 'relative',
              p: '6rem 4rem 4rem 0',
            }}
          >
            <Box
              css={height}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              {step.games?.map((games, i) => {
                return games?.map((game, index: number) => {
                  let step_name: string | undefined;
                  let step_before_style: SerializedStyles | undefined;

                  if (game?.game?.name === 'Finale') {
                    step_name = t(T_KEYS.TOURNAMENTS_FINALES_LABEL);
                  } else if (
                    typeof game?.game?.name === 'string' &&
                    game.game.name.includes('Demi')
                  ) {
                    step_name = t(T_KEYS.TOURNAMENTS_SEMI_FINALS_LABEL);
                    step_style = css`
                      &::before {
                        border: none;
                        margin: 0;
                      }

                      &:nth-child(odd):after {
                        height: 210px;
                      }
                    `;
                  } else if (
                    typeof game?.game?.name === 'string' &&
                    game.game.name.includes('Quart')
                  ) {
                    step_name = t(T_KEYS.TOURNAMENTS_QUARTER_FINALS_LABEL);
                  } else if (
                    typeof game?.game?.name === 'string' &&
                    game.game.name.includes('Huitième')
                  ) {
                    step_name = t(T_KEYS.TOURNAMENTS_ROUND_OF_SIXTEEN_LABEL);
                  }

                  if (
                    step_name === t(T_KEYS.TOURNAMENTS_FINALES_LABEL) ||
                    totalGameLength === 1
                  ) {
                    step_style = css`
                      &:nth-child(odd):after {
                        border: none;
                        margin: 0;
                      }
                    `;
                  } else if (
                    step_name === t(T_KEYS.TOURNAMENTS_SEMI_FINALS_LABEL) ||
                    totalGameLength === 2
                  ) {
                    if (firstStepLength === 2) {
                      step_before_style = css`
                        &::before {
                          border: none;
                          margin: 0;
                        }
                      `;
                      step_style = css`
                        &:nth-child(odd):after {
                          height: 25vh;
                        }
                      `;
                    }
                    if (firstStepLength === 3) {
                      step_style = css`
                        &:nth-child(odd):after {
                          height: 65vh;
                        }
                      `;
                    }
                    if (firstStepLength === 4) {
                      step_style = css`
                        &:nth-child(odd):after {
                          height: 105vh;
                        }
                      `;
                    }
                  } else if (
                    step_name === t(T_KEYS.TOURNAMENTS_QUARTER_FINALS_LABEL) ||
                    totalGameLength === 4
                  ) {
                    step_style = css`
                      &:nth-child(odd):after {
                        height: 45vh;
                      }
                    `;
                    if (firstStepLength === 3) {
                      step_before_style = css`
                        &::before {
                          border: none;
                          margin: 0;
                        }
                      `;
                      step_style = css`
                        &:nth-child(odd):after {
                          height: 20vh;
                        }
                      `;
                    }
                  } else if (
                    step_name ===
                      t(T_KEYS.TOURNAMENTS_ROUND_OF_SIXTEEN_LABEL) &&
                    firstStepLength === 4
                  ) {
                    step_before_style = css`
                      &::before {
                        border: none;
                        margin: 0;
                      }
                    `;
                  }

                  return (
                    <Box
                      css={[step_style, step_before_style]}
                      key={i}
                      sx={(theme) => ({
                        boxSizing: 'content-box',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: '1.25rem',
                        borderRadius: '8px',
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        '&::before': {
                          content: '""',
                          border: '2px solid',
                          borderColor: theme.palette.primary.main,
                          position: 'absolute',
                          left: '-18px',
                          width: '50px',
                          zIndex: 1,
                          marginTop: '5.5rem',
                        },
                        '&:nth-child(odd):after': {
                          content: '""',
                          position: 'absolute',
                          right: '18px',
                          width: '60px',
                          zIndex: 1,
                          marginTop: '5.5rem',
                          height: '18vh',
                          borderRight: '4px solid',
                          borderRightColor: theme.palette.primary.main,
                          borderTop: '4px solid',
                          borderTopColor: theme.palette.primary.main,
                          borderRadius: '0 4px 0 0',
                        },
                        '&:nth-child(even):after': {
                          borderTop: 'none',
                          content: '""',
                          position: 'absolute',
                          right: '18px',
                          width: '60px',
                          zIndex: 1,
                          marginTop: '0 !important',
                          height: '75px !important',
                          borderRight: '4px solid',
                          borderRightColor: theme.palette.primary.main,
                          borderBottom: '4px solid',
                          borderBottomColor: theme.palette.primary.main,
                          borderRadius: '0 0 4px 0',
                        },
                      })}
                    >
                      {index === 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            fontSize: '1.5rem',
                            textTransform: 'uppercase',
                            position: 'absolute',
                            top: 0,
                            margin: 0,
                            width: '100%',
                            height: '2.5rem',
                          }}
                        >
                          <h3
                            css={(theme) => css`
                              font-size: 1.5rem;
                              text-transform: uppercase;
                              text-align: center;
                              border-bottom: 3px solid
                                ${theme.palette.primary.main};
                              padding-bottom: 0.5rem;
                              position: absolute;
                              top: 0;
                              margin: 0;
                              width: 86%;
                            `}
                          >
                            {step_name}
                          </h3>
                        </Box>
                      )}
                      <EventPhaseCard
                        key={game.game?.id}
                        tournament={tournament}
                        game={{
                          id: game.game?.id,
                          place: game.game?.place,
                          status: game.game?.status,
                          date: game.game?.date,
                          time: game.game?.date,
                          is_validated_by_club: game.game?.is_validated_by_club,
                          teams: [
                            {
                              score: game.game?.home_score,
                              data: {
                                shootout: game.game?.home_shootout,
                                sets: (
                                  game.game?.home_score_data as
                                    | { sets?: unknown[] }
                                    | null
                                    | undefined
                                )?.sets,
                              },
                              name_in_competition:
                                game.game?.home_engagement?.team?.name ||
                                game.game?.home_engagement?.name,
                              ...game.game?.home_engagement?.team,
                            },
                            {
                              score: game.game?.away_score,
                              data: {
                                shootout: game.game?.away_shootout,
                                sets: (
                                  game.game?.away_score_data as
                                    | { sets?: unknown[] }
                                    | null
                                    | undefined
                                )?.sets,
                              },
                              name_in_competition:
                                game.game?.away_engagement?.team?.name ||
                                game.game?.away_engagement?.name,
                              ...game.game?.away_engagement?.team,
                            },
                          ],
                        }}
                      />
                    </Box>
                  );
                });
              })}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};
